import { Link } from "@tanstack/react-router";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { homeworkStatus, useMyHomework, type HomeworkItem } from "@/app/homeworkQueries";
import { useMe } from "@/app/session";
import { RUNTIME_LABEL, runnerAvailable } from "@/app/notebookQueries";
import { count } from "@/lib/plural";
import { StatusChip } from "@/ui/StatusChip";
import { EmptyState, SkeletonRows } from "@/ui/states";
import { NetworkError } from "./route/bits";

/** «Ноутбуки»: рабочие места домашек по маршруту — версия черновика, последний прогон, вход в S5. */
export function NotebooksPage() {
  const me = useMe();
  const hw = useMyHomework();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  return (
    <Shell nav user={user} meta="ноутбуки">
      <div className="mx-auto flex w-full max-w-[920px] flex-col gap-6 px-4 pb-20 pt-7 md:px-6">
        <header>
          <div className="mb-3 font-mono text-[11px] uppercase tracking-[.08em] text-accdim">студент · рабочее место</div>
          <h1 className="m-0 font-mono text-[28px] font-bold leading-[1.1] tracking-[-.02em] text-ink md:text-[34px]">Ноутбуки</h1>
          <p className="mb-0 mt-3 max-w-[680px] text-[13.5px] leading-[1.55] text-ink2 text-pretty">Домашки решаются в браузере на CPU, черновик сохраняется сам. Здесь — все ноутбуки курса и где вы в каждом.</p>
        </header>
        {hw.status === "pending" && <SkeletonRows rows={4} />}
        {hw.status === "error" && <NetworkError retry={() => void hw.refetch()} />}
        {hw.status === "success" && hw.data.length === 0 && (
          <EmptyState where="студент · ноутбуки" title="Ноутбуков пока нет" body="Рабочее место откроется вместе с первой домашкой: администратор прикрепляет ноутбук к шагу во вкладке «домашка» конструктора." primary={{ label: "на мой маршрут", href: "/" }} />
        )}
        {hw.status === "success" && hw.data.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-3 p-0" data-testid="notebooks">
            {hw.data.map((it) => (
              <NotebookRow key={it.assignment_id} it={it} />
            ))}
          </ul>
        )}
      </div>
      <TabBar />
    </Shell>
  );
}

export function runLine(it: HomeworkItem): string {
  if (!runnerAvailable(it.runtime)) return `без автопроверки · ${RUNTIME_LABEL[it.runtime] ?? it.runtime} — реши локально или в Colab и отправь ментору`;
  if (!it.run) return it.versions > 0 ? "автопроверка ещё не запускалась" : "не открывали";
  if (it.run.status !== "done") return it.run.status === "error" ? "последний прогон — ошибка среды" : "прогон идёт…";
  return `автопроверка ${it.run.passed} / ${it.run.total} · для отправки нужно ${it.threshold}`;
}

function NotebookRow({ it }: { it: HomeworkItem }) {
  return (
    <li className="grid grid-cols-1 gap-3 rounded-md border border-bd bg-card p-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center" data-testid="notebook-row">
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-mono text-[11px] text-mut">{it.track.toLowerCase()}</span>
          <StatusChip status={homeworkStatus(it)} compact />
        </div>
        <Link to="/step/$id" params={{ id: it.step_id }} className="font-mono text-[15px] font-semibold text-ink no-underline hover:underline">
          {it.step_title}
        </Link>
        <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11.5px] text-ink2">
          <span>{it.file_name}</span>
          <span>·</span>
          <span>{count(it.tasks, "задача", "задачи", "задач")}</span>
          <span>·</span>
          <span>{it.versions > 0 ? `черновик v${it.versions}` : "черновика нет"}</span>
        </div>
        <div className="font-mono text-[11.5px] text-mut" data-testid="run-line">
          {runLine(it)}
        </div>
      </div>
      <Link to="/notebook/$assignmentId" params={{ assignmentId: it.assignment_id }} className="inline-flex items-center justify-center rounded-sm border border-bd2 bg-accsoft px-3.5 py-2 font-mono text-[12.5px] text-acc no-underline hover:border-acc">
        {it.versions > 0 ? "продолжить ноутбук" : "открыть ноутбук"}
      </Link>
    </li>
  );
}
